import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Stack, Typography } from '@mui/material';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { PerformanceOverview, TimeFormatKeys } from './campaign-statistics-types';
import StatisticsSectionTitle from './statistics-section-title';

interface ItemProps {
  title: string;
  content: {
    current?: number;
    total?: number;
    percentage?: number;
    text?: string;
  };
}

const StatisticItem: React.FC<ItemProps> = ({ title, content }) => {
  const { classes } = useStyles();

  const renderContent = () => {
    if (!_.isNil(content.percentage)) {
      return `${(content.percentage * 100).toFixed(1)}%`;
    }
    if (content.current && content.total) {
      return `${content.current} / ${content.total}`;
    }
    if (content.current) {
      return content.current;
    }
    if (content.text) {
      return content.text;
    }
    return '-';
  };

  return (
    <Stack className={classes.statisticItem} gap={'4px'}>
      <Typography className={classes.statisticItemTitle}>{title}</Typography>
      <Typography className={classes.statisticItemValue}>{renderContent()}</Typography>
    </Stack>
  );
};

interface Props {
  performanceOverview: PerformanceOverview | undefined;
  loading: boolean;
}

const StatisticsPerformanceOverview: React.FC<Props> = ({ performanceOverview, loading }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const {
    sentConnectionRequestsCount = 0,
    acceptedConnectionRequestsCount = 0,
    avgConnectionRequestAcceptanceTime = 0,
    contactedContactsCount = 0,
    messageReplyCount = 0,
    positiveReplyCount = 0,
    negativeReplyCount = 0,
    avgLeadToConversionTime = 0,
    hasConnectionRequests = false,
  } = performanceOverview ?? {};

  function formatConversionTime(seconds: number) {
    const secondsInHour = 3600;
    const secondsInMinute = 60;
    const secondsInDay = secondsInHour * 24;
    const secondsInWeek = secondsInDay * 7;

    const weeks = Math.floor(seconds / secondsInWeek);
    const days = Math.floor((seconds % secondsInWeek) / secondsInDay);
    const hours = Math.floor((seconds % secondsInDay) / secondsInHour);
    const minutes = Math.floor((seconds % secondsInHour) / secondsInMinute);

    const parts = [];
    if (weeks > 0) parts.push(t(weeks > 1 ? TimeFormatKeys.WEEKS : TimeFormatKeys.WEEK, { count: weeks }));
    if (days > 0) parts.push(t(days > 1 ? TimeFormatKeys.DAYS : TimeFormatKeys.DAY, { count: days }));
    if (hours > 0) parts.push(t(hours > 1 ? TimeFormatKeys.HOURS : TimeFormatKeys.HOUR, { count: hours }));
    if (minutes > 0) parts.push(t(minutes > 1 ? TimeFormatKeys.MINUTES : TimeFormatKeys.MINUTE, { count: minutes }));

    return parts.join(', ') || '-';
  }

  const calculatePercentage = (current: number, total: number) => (total ? current / total : undefined);

  return (
    <div className={classes.statisticsPerformanceOverview}>
      {loading ? (
        <Stack className={classes.loadingContainer} justifyContent="center" alignItems="center">
          <PaiSpinningLoader size="large" />
        </Stack>
      ) : null}

      <StatisticsSectionTitle icon={<TrendingUpIcon />} title={t('campaignsPerformanceOverview')} />

      <Stack direction={'row'} gap={2} className={classes.cards}>
        {hasConnectionRequests ? (
          <Box className={classes.card}>
            <Typography className={classes.cardTitle}>{t('networkExpansion')}</Typography>
            <div className={classes.grid}>
              <StatisticItem title={t('sentConnectionRequests')} content={{ current: sentConnectionRequestsCount }} />
              <StatisticItem
                title={t('acceptedConnectionRequests')}
                content={{ current: acceptedConnectionRequestsCount, total: sentConnectionRequestsCount }}
              />
              <StatisticItem
                title={t('connectionRequestsRate')}
                content={{ percentage: calculatePercentage(acceptedConnectionRequestsCount, sentConnectionRequestsCount) }}
              />
              <StatisticItem title={t('avgConnectionRequestAcceptanceTime')} content={{ text: formatConversionTime(avgConnectionRequestAcceptanceTime) }} />
            </div>
          </Box>
        ) : null}
        <Box className={classes.card}>
          <Typography className={classes.cardTitle}>{t('responseRates')}</Typography>
          <div className={classes.grid}>
            <StatisticItem title={t('contactedContacts')} content={{ current: contactedContactsCount }} />
            <StatisticItem title={t('totalReplies')} content={{ current: messageReplyCount, total: contactedContactsCount }} />
            <StatisticItem title={t('positiveReplies')} content={{ percentage: calculatePercentage(positiveReplyCount, messageReplyCount) }} />
            <StatisticItem title={t('negativeReplies')} content={{ percentage: calculatePercentage(negativeReplyCount, messageReplyCount) }} />
            <StatisticItem title={t('responseRate')} content={{ percentage: calculatePercentage(messageReplyCount, contactedContactsCount) }} />
            <StatisticItem title={t('avgLeadToConversionTime')} content={{ text: formatConversionTime(avgLeadToConversionTime) }} />
          </div>
        </Box>
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  statisticsPerformanceOverview: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#FFF',
    opacity: 0.8,
    zIndex: 90,
  },
  cards: {
    alignItems: 'stretch',
  },
  card: {
    flex: 1,
    background: '#FFF',
    borderRadius: '12px',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
    color: theme.palette.greys.medium,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  statisticItem: {
    padding: '14px 16px',
    background: theme.palette.backgrounds.main_new,
    borderRadius: '10px',
  },
  statisticItemTitle: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    lineHeight: 1.35,
  },
  statisticItemValue: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
  },
}));

export default StatisticsPerformanceOverview;
